import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { Building, Shield, Users, FileText, Loader2 } from 'lucide-react';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, loading, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/staff/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    try {
      await login();
      // Navigation will happen via useEffect when isAuthenticated becomes true
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sunbeth-orange-50 to-sunbeth-orange-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-20 w-20 flex items-center justify-center">
            <img 
              src="/logo192.png" 
              alt="Sunbeth Global Concepts" 
              className="h-20 w-20 object-contain"
            />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Vendor Management Portal
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sunbeth Global Concepts Limited
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-lg">
          <div className="space-y-6">
            {/* Welcome Message */}
            <div className="text-center">
              <Shield className="mx-auto h-12 w-12 text-sunbeth-orange-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                Staff Authentication Required
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Please sign in with your Microsoft 365 organizational account to access the vendor management system.
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-900">Access Features:</h4>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-sunbeth-orange-600 mr-3" />
                  <span className="text-sm text-gray-600">View and manage vendor applications</span>
                </div>
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-sunbeth-orange-600 mr-3" />
                  <span className="text-sm text-gray-600">Review vendor documents and details</span>
                </div>
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-sunbeth-orange-600 mr-3" />
                  <span className="text-sm text-gray-600">Approve or reject vendor applications</span>
                </div>
              </div>
            </div>

            {/* Login Button */}
            <div>
              <button
                onClick={handleLogin}
                disabled={loading}
                className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-sunbeth-orange-600 hover:bg-sunbeth-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sunbeth-orange-500'
                } transition duration-150 ease-in-out`}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5 mr-2"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z" />
                    </svg>
                    Sign in with Microsoft 365
                  </>
                )}
              </button>
            </div>

            {/* Security Notice */}
            <div className="bg-sunbeth-orange-50 border border-sunbeth-orange-200 rounded-md p-4">
              <div className="flex">
                <Shield className="h-5 w-5 text-sunbeth-orange-400 mr-2 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-sunbeth-orange-800">Secure Access</h4>
                  <p className="text-sm text-sunbeth-orange-700 mt-1">
                    This system uses Microsoft 365 authentication to ensure only authorized PMO staff can access vendor information.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            © 2024 Sunbeth Vendor Management System. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
